<script setup lang="ts">
    import Highcharts from 'highcharts';
    import VariablePie from 'highcharts/modules/variable-pie';
    import Drilldown from 'highcharts/modules/drilldown';
    import { Chart } from 'highcharts-vue';
    import type {
        Options,
        Point,
        SeriesOptionsType,
        TooltipFormatterCallbackFunction,
        DataLabelsFormatterCallbackFunction,
    } from 'highcharts';
    import { computed } from 'vue';
    import { IPieChart } from '@/js/services/api/widget/types';
    import { __ } from '../../../stores/expressions';
    import BaseChart from './BaseChart.vue';

    type VariablePiePoint = Point & { z: number };
    // Highcharts declares formatter `this` as `Point`, but the runtime context also
    // provides `this.point` and `this.series`. We cast at the assignment boundary to
    // acknowledge the inaccuracy while keeping the function body properly typed.
    type PieFormatterContext = {
        series: { name: string; options: { id?: string } };
        key: string | number;
        point: VariablePiePoint;
    };

    // Modules may be wrapped in a default export depending on bundler
    const initVariablePie = (VariablePie as any).default ?? VariablePie;
    if (typeof initVariablePie === 'function') {
        initVariablePie(Highcharts);
    }

    const initDrilldown = (Drilldown as any).default ?? Drilldown;
    if (typeof initDrilldown === 'function') {
        initDrilldown(Highcharts);
    }

    const props = defineProps<{
        data: IPieChart['pieChart'] | null;
    }>();

    const chartColors: string[] = [
        'var(--color-primary-beet)',
        'var(--color-primary-raspberry)',
        'var(--color-primary-plum)',
        'var(--color-primary-carrot)',
        'var(--color-primary-avocado)',
        'var(--color-primary-lime)',
        'var(--color-primary-blueberry)',
        'var(--color-primary-acai)',
        'var(--color-primary-plum-400)',
        'var(--color-primary-carrot-400)',
    ];

    const hasData = computed(() => !!props.data?.data?.length);

    const unitType = computed(() =>
        props.data?.unitType === '%' ? __('percentage-character') : ''
    );

    const mainSeries = computed(() => {
        if (!props.data) return [];
        const data = props.data;

        return data.data
            .filter((item) => item.parentId == null || item.parentId === '')
            .map((item) => ({
                id: item.id,
                name: item.name.trim(),
                y: item.count, // width/angle — weighting in points
                z: data.unitType === '%' ? parseFloat(item.value.toFixed(1)) : item.value, // radius — result value
                drilldown: data.data.some((x) => x.parentId === item.id) ? item.id : undefined,
            }));
    });

    const bgSeries = computed(() => {
        const total = mainSeries.value.reduce((sum, p) => sum + p.y, 0);
        return { total };
    });

    const drilldownSeries = computed((): SeriesOptionsType[] => {
        if (!props.data) return [];
        const data = props.data;

        const parentIds = Array.from(
            new Set(
                data.data
                    .map((item) => item.parentId)
                    .filter((parentId): parentId is string => !!parentId && parentId !== '')
            )
        );

        return parentIds.map((id) => ({
            type: 'variablepie' as const,
            id,
            center: ['50%', '50%'] as [string, string],
            size: '75%',
            zMin: 0,
            zMax: 100,
            minPointSize: 5,
            borderWidth: 0,
            dataLabels: {
                enabled: true,
                formatter: function (this: PieFormatterContext) {
                    return `${this.key}<br/>${this.point.z}${unitType.value ? `\u00a0${unitType.value}` : ''}`;
                } as unknown as DataLabelsFormatterCallbackFunction,
                style: {
                    fontSize: '12px',
                    fontWeight: 'inherit',
                },
            },
            data: data.data
                .filter((item) => item.parentId === id)
                .map((item) => ({
                    id: item.id,
                    name: item.name.trim(),
                    y: item.count,
                    z: data.unitType === '%' ? parseFloat(item.value.toFixed(1)) : item.value,
                    drilldown: data.data.some((x) => x.parentId === item.id) ? item.id : undefined,
                })),
        })) as SeriesOptionsType[];
    });

    const chartOptions = computed<Options>(() => ({
        chart: {
            type: 'variablepie',
            style: { fontFamily: 'inherit' },
        },
        title: { text: '' },
        colors: chartColors,
        tooltip: {
            formatter: function (this: PieFormatterContext) {
                if (this.series?.options?.id === 'bg') return false;
                return `${this.key}: ${this.point.z}${unitType.value ? ` ${unitType.value}` : ''}`;
            } as unknown as TooltipFormatterCallbackFunction,
        },
        legend: { enabled: false },
        exporting: { enabled: false },
        credits: { enabled: false },
        plotOptions: {
            series: {
                cursor: 'pointer',
                clip: false,
            },
        },
        series: [
            // Background series: reference circle representing 100% result
            {
                type: 'variablepie',
                id: 'bg',
                name: 'BG',
                center: ['50%', '50%'],
                size: '75%',
                zMin: 0,
                zMax: 100,
                minPointSize: 0,
                borderWidth: 0,
                dataLabels: { enabled: false },
                enableMouseTracking: false,
                states: { hover: { enabled: false }, inactive: { opacity: 1 } },
                data: [
                    {
                        y: bgSeries.value.total,
                        z: 100,
                        color: 'var(--color-gray-extralight)',
                    },
                ],
            } as unknown as SeriesOptionsType,
            {
                type: 'variablepie',
                name: __('chart-main-serie'),
                center: ['50%', '50%'],
                size: '75%',
                zMin: 0,
                zMax: 100,
                minPointSize: 5,
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    formatter: function (this: PieFormatterContext) {
                        return `${this.key}<br/>${this.point.z}${unitType.value ? `\u00a0${unitType.value}` : ''}`;
                    } as unknown as DataLabelsFormatterCallbackFunction,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'inherit',
                    },
                },
                data: mainSeries.value,
            } as unknown as SeriesOptionsType,
        ],
        drilldown: {
            breadcrumbs: {
                enabled: true,
                style: {
                    fontFamily: 'inherit !important',
                },
                buttonTheme: {
                    style: {
                        textDecoration: 'none',
                        color: 'inherit',
                        fontFamily: 'inherit !important',
                    },
                    states: {
                        hover: {
                            fill: 'var(--color-gray-lightest)',
                        },
                    },
                },
            },
            activeDataLabelStyle: {
                color: 'var(--color-gray-dark)',
                textDecoration: 'none',
                fontWeight: 'inherit',
                cursor: 'pointer',
            },
            series: drilldownSeries.value,
        },
    }));
</script>

<template>
    <div class="Chart__container" style="--chart-background-color: var(--color-gray-lightest)">
        <BaseChart :has-data="hasData">
            <Chart v-if="hasData" :options="chartOptions" :highcharts="Highcharts" />
        </BaseChart>
    </div>
</template>
